<template>
  <div>
    <breadcrumb :items="breadcrumbs" />
    <v-container fluid>
      <div class="d-flex mb-6 align-center">
        <h2 class="font-weight-bold">{{ $t('carbonCredit.manageTrees.pageTitle') }}</h2>
        <v-spacer></v-spacer>
        <v-btn outlined color="primary" class="mr-3" @click="$router.push({ name: 'bulkImportTrees' })">
          <v-icon left>mdi-download</v-icon>
          {{ $t('carbonCredit.manageTrees.importInBulk') }}
        </v-btn>
        <v-btn color="primary" dark @click="addNewTree">
          {{ $t('carbonCredit.manageTrees.addNewTree') }}
        </v-btn>
      </div>

      <!-- Map Section -->
      <v-card elevation="0" class="mb-6" style="border: 1px dashed #90caf9; border-radius: 8px;">
        <div style="position: relative;">
          <GmapMap
            ref="mapRef"
            :center="mapCenter"
            :zoom="7"
            :map-type-id="mapType"
            style="width: 100%; height: 430px;"
          >
            <GmapInfoWindow
              v-if="selectedCluster"
              :position="selectedCluster.position"
              :opened="!!selectedCluster"
              @closeclick="selectedCluster = null"
            >
              <div class="cluster-info-window">
                <strong>{{ $t('carbonCredit.manageTrees.clusterDetails') }}</strong>
                <div>{{ $t('carbonCredit.manageTrees.trees') }}: {{ selectedCluster.count }}</div>
                <div>{{ $t('carbonCredit.manageTrees.primaryTreeType') }}: {{ selectedCluster.primaryType }}</div>
              </div>
            </GmapInfoWindow>
            <GmapCluster>
              <GmapMarker
                v-for="(marker, idx) in mapMarkers"
                :key="`tree-marker-${marker.id || idx}`"
                :position="marker.position"
                :icon="marker.icon"
                :clickable="true"
                :title="marker.label"
                @click="onMarkerClick(marker)"
              />
            </GmapCluster>
          </GmapMap>
        </div>
      </v-card>

      <!-- Tree List Section -->
      <div class="mb-3">
        <h3 class="font-weight-bold">{{ $t('carbonCredit.manageTrees.treeList') }}</h3>
      </div>

      <v-card elevation="0" class="px-5">
        <v-card-text>
          <!-- Filters Row -->
          <v-row class="mb-2" align="center">
            <v-col cols="12" sm="3">
              <div class="filter-label mb-1">{{ $t('carbonCredit.manageTrees.treeId') }}</div>
              <v-text-field
                prepend-inner-icon="mdi-magnify"
                outlined
                :placeholder="$t('carbonCredit.manageTrees.search')"
                dense
                v-model="searchTreeId"
                @input="searchQuery"
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="3">
              <div class="filter-label mb-1">{{ $t('carbonCredit.manageTrees.project') }}</div>
              <v-select
                v-model="selectedProject"
                :items="projects"
                item-text="name"
                item-value="id"
                outlined
                dense
                :placeholder="$t('carbonCredit.manageTrees.makeASelection')"
                clearable
                hide-details
                @change="filterData"
              ></v-select>
            </v-col>
          </v-row>

          <!-- Table Actions Row -->
          <v-row class="mb-2" align="center">
            <v-col cols="auto">
              <v-btn
                color="primary"
                :disabled="selectedTrees.length === 0"
                @click="downloadSelectedTrees"
              >
                {{ $t('carbonCredit.manageTrees.downloadTrees') }}
              </v-btn>
            </v-col>
            <v-col cols="auto" v-if="selectedTrees.length > 0">
              <span class="font-weight-bold">{{ selectedTrees.length }} Selected</span>
            </v-col>
            <v-spacer></v-spacer>
            <!-- Pagination Info -->
            <v-col cols="auto" class="py-1 title">
              {{ fromTrees }} of {{ totalTrees }}
            </v-col>
            <!-- Pagination Controls -->
            <v-col cols="auto">
              <v-btn fab small outlined color="primary" :disabled="options.page <= 1" @click="pageChange(false)">
                <v-icon dark>mdi-chevron-left</v-icon>
              </v-btn>
            </v-col>
            <v-col cols="auto">
              <v-btn fab small outlined color="primary" :disabled="options.page >= totalPages" @click="pageChange(true)">
                <v-icon dark>mdi-chevron-right</v-icon>
              </v-btn>
            </v-col>
            <!-- Customization Menu -->
            <v-col cols="auto">
              <v-menu v-model="menu" :close-on-content-click="false" :nudge-width="200" left :min-width="417">
                <template v-slot:activator="props">
                  <v-btn fab small outlined color="primary" v-bind="props.attrs" v-on="props.on">
                    <v-icon dark>mdi-cog-outline</v-icon>
                  </v-btn>
                </template>
                <v-card width="417">
                  <v-list>
                    <v-list-item>
                      <v-list-item-title>{{ $t("customization") }}</v-list-item-title>
                    </v-list-item>
                    <v-divider></v-divider>
                    <div class="pl-3 pt-2 font-weight-normal">{{ $t("recordsPerPage") }}</div>
                    <v-list-item>
                      <v-slider v-model="itemsPerPageSlider" :tick-labels="itemsPerPageOptions" :max="3" step="1"
                        ticks="always" tick-size="4"></v-slider>
                    </v-list-item>
                    <v-divider class="mt-n3"></v-divider>
                  </v-list>
                  <v-card-text>
                    <v-container fluid>
                      <v-row>
                        <v-col class="mt-n9" cols="6" v-for="header in headers" :key="header.value">
                          <v-checkbox v-if="header.text" v-model="selectedHeaders"
                            :label="$t('carbonCredit.manageTrees.' + header.text)"
                            color="primary" :value="header.text" hide-details></v-checkbox>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-card-text>
                  <v-layout justify-center>
                    <v-card-actions class="mb-5">
                      <v-spacer></v-spacer>
                      <v-btn outlined color="primary" @click="resetDefault" width="190">
                        {{ $t("restoreDefault") }}
                      </v-btn>
                      <v-btn color="primary" @click="applyTableStructure" width="190">
                        {{ $t("apply") }}
                      </v-btn>
                    </v-card-actions>
                  </v-layout>
                </v-card>
              </v-menu>
            </v-col>
            <v-col cols="auto">
              <v-tooltip top color="black">
                <template v-slot:activator="props">
                  <v-btn fab small outlined color="primary" v-bind="props.attrs" v-on="props.on" @click="exportToPDF">
                    <v-img src="/icons/download.svg"></v-img>
                  </v-btn>
                </template>
                <span>{{ $t("download") }}</span>
              </v-tooltip>
            </v-col>
            <v-col cols="auto">
              <v-tooltip top color="black">
                <template v-slot:activator="props">
                  <v-btn fab small outlined color="primary" v-bind="props.attrs" v-on="props.on" @click="printTable">
                    <v-icon dark>mdi-printer</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("print") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>

          <!-- Data Table -->
          <v-data-table
            :headers="displayHeaders"
            :items="trees"
            :items-per-page="options.limit"
            :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
            item-key="id"
            hide-default-footer
            :loading="loading"
            :options.sync="options"
            :server-items-length="totalTrees"
            :loading-text="$t('loadingData')"
            show-select
            class="trees-table"
          >
            <!-- Custom header checkbox with dropdown -->
            <template v-slot:header.data-table-select>
              <div class="d-flex align-center">
                <v-checkbox
                  :input-value="allOnPageSelected"
                  color="white"
                  hide-details
                  dense
                  class="mt-0 pt-0"
                  @change="onSelectAllPage"
                ></v-checkbox>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon small color="white" class="ml-n1" v-bind="attrs" v-on="on">
                      mdi-menu-down
                    </v-icon>
                  </template>
                  <v-list dense>
                    <v-list-item @click="selectCurrentPage">
                      <v-list-item-title>{{ $t('carbonCredit.manageTrees.currentPage') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="selectAllPages">
                      <v-list-item-title>{{ $t('carbonCredit.manageTrees.allPages') }}</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="clearSelection">
                      <v-list-item-title>{{ $t('carbonCredit.manageTrees.none') }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </div>
            </template>

            <template v-if="trees.length" v-slot:body="{ items }">
              <tbody>
                <tr
                  v-for="item in items"
                  :key="item.id"
                  style="position: relative; width: 0; height: 0"
                  @mouseover="hoveredItem = item"
                  @mouseleave="hoveredItem = null"
                >
                  <td>
                    <v-checkbox
                      :input-value="isTreeSelected(item.id)"
                      color="primary"
                      hide-details
                      dense
                      class="mt-0 pt-0"
                      @change="toggleTreeSelection(item)"
                    ></v-checkbox>
                  </td>
                  <template v-for="h in displayHeaders">
                    <td :key="h.value + '_treeId'" v-if="h.value === 'treeId'" class="text-truncate">
                      {{ item.tree_id_by_user || item.id || '-' }}
                    </td>
                    <td :key="h.value + '_treeType'" v-else-if="h.value === 'treeType'" class="text-truncate">
                      <div>{{ item.treeType?.name || '-' }}</div>
                      <div class="caption grey--text">{{ item.treeSpecies?.name || '' }}</div>
                    </td>
                    <td :key="h.value + '_geoCoordinates'" v-else-if="h.value === 'geoCoordinates'" class="text-truncate">
                      {{ item.lat ?? '-' }}, {{ item.lon ?? '-' }}
                    </td>
                    <td :key="h.value + '_addedBy'" v-else-if="h.value === 'addedBy'" class="text-truncate">
                      <div>{{ item.user?.fullName || '-' }}</div>
                      <div class="caption grey--text" v-if="item.user?.id">(#{{ item.user.id }})</div>
                    </td>
                    <td :key="h.value + '_datePlanted'" v-else-if="h.value === 'datePlanted'" class="text-truncate">
                      {{ formatDate(item.date_planted) }}
                    </td>
                    <td :key="h.value + '_action'" v-else-if="h.value === 'action'">
                      <div style="position: absolute; right: 10px; top: 10px;">
                        <v-menu location="start">
                          <template v-slot:activator="props">
                            <v-btn icon v-bind="props.attrs" v-on="props.on">
                              <v-icon>mdi-dots-vertical</v-icon>
                            </v-btn>
                          </template>
                          <v-list>
                            <v-list-item @click="viewTree(item)">
                              <v-list-item-title>{{ $t('carbonCredit.manageTrees.view') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="editTree(item)">
                              <v-list-item-title>{{ $t('carbonCredit.manageTrees.edit') }}</v-list-item-title>
                            </v-list-item>
                            <v-list-item @click="confirmDeleteTree(item)">
                              <v-list-item-title style="color: red;">{{ $t('carbonCredit.manageTrees.delete') }}</v-list-item-title>
                            </v-list-item>
                          </v-list>
                        </v-menu>
                      </div>
                    </td>
                  </template>
                </tr>
              </tbody>
            </template>
            <template v-slot:footer></template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Delete Confirmation Dialog -->
    <ConfirmBox
      :title="confirmAction.title"
      :message="confirmAction.message"
      :cancelBtnText="confirmAction.cancelBtnText"
      :confirmBtnText="confirmAction.confirmBtnText"
      :confirmData="confirmAction.confirmData"
      :dialog="confirmActionDialog"
      @cancel:action="confirmActionDialog = false"
      @continue:action="deleteTree"
    />

    <!-- Import Bulk Dialog -->
    <v-dialog v-model="importBulkDialog" max-width="500">
      <v-card>
        <v-card-title class="primary white--text">
          {{ $t('carbonCredit.manageTrees.importTreesInBulk') }}
          <v-spacer></v-spacer>
          <v-btn icon color="white" @click="importBulkDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4">
          <v-file-input
            v-model="importFile"
            :label="$t('carbonCredit.manageTrees.uploadCsvOrExcel')"
            accept=".csv,.xlsx,.xls"
            outlined
            dense
            prepend-icon="mdi-paperclip"
          ></v-file-input>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn outlined @click="importBulkDialog = false">{{ $t('carbonCredit.manageTrees.cancel') }}</v-btn>
          <v-btn color="primary" :disabled="!importFile" @click="submitImport">{{ $t('carbonCredit.manageTrees.import') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import loadingMixin from "@/mixins/LoadingMixin";
import download from "@/mixins/DownloadMixin";
import getPermittedActions from "@/mixins/GetPermittedActions";
import DateMixin from "@/mixins/DateMixin";
import RoleMixin from "@/mixins/RoleMixin";
import CarbonCreditService from "@/_services/CarbonCreditService";
import ConfirmBox from "@/components/ConfirmBox";
import moment from "moment";
import html2pdf from "html2pdf.js";
import printJS from "print-js";
import GmapCluster from "vue2-google-maps/dist/components/cluster";

export default {
  name: "ManageTreesList",
  components: { ConfirmBox, GmapCluster },
  mixins: [loadingMixin, download, getPermittedActions, DateMixin, RoleMixin],

  created() {
    this.selectedTrees = [];
    this.fetchTrees();
    this.fetchProjects();
  },

  activated() {
    this.selectedTrees = [];
  },

  mounted() {
    this.tableHeaders = this.headers;
    this.selectedHeaders = this.allSelectedHeaders;
  },

  watch: {
    options: {
      handler() {
        this.fetchTrees();
      },
      deep: true,
    },
    selectedTrees: {
      handler() {
        this.updateMapViewportToSelection();
      },
      deep: true,
    },
  },

  computed: {
    totalPages() {
      return Math.ceil(this.totalTrees / this.options.limit) || 1;
    },
    displayHeaders() {
      return this.tableHeaders.map(h => ({
        ...h,
        text: h.text ? this.$t('carbonCredit.manageTrees.' + h.text) : h.text,
      }));
    },
    allOnPageSelected: {
      get() {
        return (
          Array.isArray(this.trees) &&
          this.trees.length > 0 &&
          this.trees.every(t => this.selectedTrees.some(s => s.id === t.id))
        );
      },
      set(val) {
        if (val) this.selectCurrentPage();
        else this.clearSelection();
      },
    },
    mapMarkers() {
      const sourceTrees = this.selectedTrees.length
        ? this.selectedTrees
        : (Array.isArray(this.trees) ? this.trees : []);
      return sourceTrees
        .filter(t => t.lat && t.lon)
        .map(t => ({
          id: t.id,
          position: { lat: parseFloat(t.lat), lng: parseFloat(t.lon) },
          label: t.tree_id_by_user || String(t.id),
          treeType: t.treeType?.name || "-",
          icon: "http://maps.google.com/mapfiles/ms/icons/green-dot.png",
        }));
    },
  },

  data() {
    return {
      debounceTimer: null,
      loading: false,
      trees: [],
      totalTrees: 0,
      fromTrees: 0,
      selectedTrees: [],
      searchTreeId: "",
      selectedProject: null,
      projects: [],
      options: {
        limit: 30,
        page: 1,
      },
      headers: [
        { text: "treeIdHeader", align: "start", value: "treeId", class: "black--text", sortable: true },
        { text: "treeTypeAndSpecies", align: "start", value: "treeType", class: "black--text", sortable: true },
        { text: "geoCoordinates", align: "start", value: "geoCoordinates", class: "black--text", sortable: false },
        { text: "addedBy", align: "start", value: "addedBy", class: "black--text", sortable: true },
        { text: "datePlanted", align: "start", value: "datePlanted", class: "black--text", sortable: true },
        { align: "center", value: "action", class: "black--text", sortable: false },
      ],
      allSelectedHeaders: ["treeIdHeader", "treeTypeAndSpecies", "geoCoordinates", "addedBy", "datePlanted"],
      tableHeaders: [],
      selectedHeaders: [],
      menu: false,
      itemsPerPageOptions: [5, 10, 25, 50],
      itemsPerPageSlider: 2,
      mapCenter: { lat: -20.5, lng: -46.5 },
      mapType: "satellite",
      selectedCluster: null,
      hoveredItem: null,
      importBulkDialog: false,
      importFile: null,
      confirmActionDialog: false,
      confirmAction: {
        title: "",
        message: "",
        confirmBtnText: "",
        cancelBtnText: "",
        confirmData: {},
      },
      breadcrumbs: [
        { text: "Dashboard", disabled: false, href: "/dashboard", exact: true },
        { text: "Carbon Credit", disabled: false, href: "/carbon-credit/projects" },
        { text: "Manage Trees", disabled: true },
      ],
    };
  },

  methods: {
    getTreeQueryParams() {
      return {
        search: this.searchTreeId || undefined,
        projectId: this.selectedProject || undefined,
      };
    },

    async fetchTrees() {
      this.loading = true;
      try {
        const params = {
          page: this.options.page,
          limit: this.options.limit,
          ...this.getTreeQueryParams(),
        };
        const res = await CarbonCreditService.getTrees(params);
        const responseData = res?.data?.data ?? res?.data ?? {};
        const treesPayload = responseData?.trees ?? responseData?.rows ?? [];
        this.trees = Array.isArray(treesPayload) ? treesPayload : [];
        this.totalTrees =
          typeof responseData?.total === "number"
            ? responseData.total
            : this.trees.length;
        this.fromTrees = this.totalTrees > 0
          ? (this.options.page - 1) * this.options.limit + 1
          : 0;
      } catch (err) {
        console.error("Error fetching trees", err);
        this.trees = [];
        this.totalTrees = 0;
      } finally {
        this.loading = false;
      }
    },

    async fetchProjects() {
      try {
        const res = await CarbonCreditService.getTreeDropdownValues();
        const data = res?.data || res;
        this.projects = data?.programTypes || [];
      } catch (err) {
        console.error("Error fetching projects", err);
      }
    },

    searchQuery() {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.options.page = 1;
        this.fetchTrees();
      }, 500);
    },

    filterData() {
      this.options.page = 1;
      this.fetchTrees();
    },

    pageChange(next) {
      if (next) this.options.page += 1;
      else this.options.page -= 1;
      this.fetchTrees();
    },

    formatDate(date) {
      return date ? moment(date).format("DD-MMM-YYYY").toUpperCase() : "-";
    },

    selectCurrentPage() {
      const currentPageIds = new Set(this.selectedTrees.map(t => t.id));
      this.trees.forEach(t => {
        if (!currentPageIds.has(t.id)) this.selectedTrees.push(t);
      });
    },

    isTreeSelected(treeId) {
      return this.selectedTrees.some(t => t.id === treeId);
    },

    toggleTreeSelection(item) {
      const idx = this.selectedTrees.findIndex(t => t.id === item.id);
      if (idx >= 0) {
        this.selectedTrees.splice(idx, 1);
        return;
      }
      this.selectedTrees.push(item);
    },

    async selectAllPages() {
      if (!this.totalTrees) {
        this.$notify({ text: this.$t('carbonCredit.manageTrees.none'), type: "info" });
        return;
      }

      this.startLoading();
      try {
        const totalPagesToLoad = Math.ceil(this.totalTrees / this.options.limit) || 1;
        const existingMap = new Map(this.selectedTrees.map((tree) => [tree.id, tree]));

        for (let page = 1; page <= totalPagesToLoad; page += 1) {
          const params = {
            page,
            limit: this.options.limit,
            ...this.getTreeQueryParams(),
          };
          const res = await CarbonCreditService.getTrees(params);
          const responseData = res?.data?.data ?? res?.data ?? {};
          const treesPayload = responseData?.trees ?? responseData?.rows ?? [];
          if (!Array.isArray(treesPayload)) continue;

          treesPayload.forEach((tree) => {
            if (tree?.id != null) existingMap.set(tree.id, tree);
          });
        }

        this.selectedTrees = Array.from(existingMap.values());
      } catch (err) {
        console.error("Error selecting all pages", err);
        this.$notify({ text: this.$t("somethingWentWrong"), type: "error" });
      } finally {
        this.stopLoading();
      }
    },

    clearSelection() {
      this.selectedTrees = [];
    },

    onSelectAllPage(val) {
      if (val) this.selectCurrentPage();
      else this.clearSelection();
    },

    updateMapViewportToSelection() {
      this.$nextTick(() => {
        const mapComponent = this.$refs.mapRef;
        const mapInstance = mapComponent && typeof mapComponent.$mapPromise === "object"
          ? mapComponent.$mapPromise
          : null;
        if (!mapInstance) return;

        mapInstance.then((map) => {
          const markers = this.mapMarkers;
          if (!markers.length) return;

          if (markers.length === 1) {
            map.setCenter(markers[0].position);
            map.setZoom(12);
            return;
          }

          const bounds = new window.google.maps.LatLngBounds();
          markers.forEach((marker) => bounds.extend(marker.position));
          map.fitBounds(bounds);
        });
      });
    },

    onMarkerClick(marker) {
      this.selectedCluster = {
        position: marker.position,
        count: 1,
        primaryType: marker.treeType || "Unknown",
      };
    },

    addNewTree() {
      this.$router.push({ name: "createTree" });
    },

    viewTree(item) {
      this.$router.push({ name: "viewTree", params: { id: item.id } });
    },

    editTree(item) {
      this.$router.push({ name: "editTree", params: { id: item.id } });
    },

    confirmDeleteTree(item) {
      this.confirmAction.title = this.$t('carbonCredit.manageTrees.deleteTree');
      this.confirmAction.message = this.$t('carbonCredit.manageTrees.deleteTreeConfirm');
      this.confirmAction.confirmBtnText = this.$t('carbonCredit.manageTrees.delete');
      this.confirmAction.cancelBtnText = this.$t('carbonCredit.manageTrees.cancel');
      this.confirmAction.confirmData = { id: item.id };
      this.confirmActionDialog = true;
    },

    async deleteTree(action, confirmData) {
      this.confirmActionDialog = false;
      try {
        this.startLoading();
        await CarbonCreditService.deleteTree(confirmData.id);
        this.$notify({ text: this.$t('carbonCredit.manageTrees.treeDeletedSuccessfully'), type: "success" });
        this.fetchTrees();
      } catch (err) {
        this.$notify({ text: err?.response?.data?.message || this.$t('carbonCredit.manageTrees.treeDeleteFailed'), type: "error" });
      }
      finally {
        this.stopLoading();
      }
    },

    async downloadSelectedTrees() {
      if (!this.selectedTrees.length) return;
      try {
        this.startLoading();
        this.$notify({ text: this.$t('carbonCredit.manageTrees.downloadingTrees'), type: "info" });
        const treeIds = this.selectedTrees.map((t) => t.id).filter(Boolean);
        await CarbonCreditService.downloadTreesPdf(treeIds);
      } catch (err) {
        this.$notify({
          text: err?.data?.message || this.$t("somethingWentWrong"),
          type: "error",
        });
      } finally {
        this.stopLoading();
      }
    },

    openImportBulk() {
      this.importBulkDialog = true;
    },

    submitImport() {
      this.$notify({ text: this.$t('carbonCredit.manageTrees.importStarted'), type: "info" });
      this.importBulkDialog = false;
      this.importFile = null;
    },

    resetDefault() {
      this.tableHeaders = this.headers;
      this.selectedHeaders = this.allSelectedHeaders;
      this.menu = false;
      this.fetchTrees();
    },

    applyTableStructure() {
      this.tableHeaders = this.headers.filter(h =>
        (h.text && this.selectedHeaders.includes(h.text)) || !h.text
      );
      this.options.limit = this.itemsPerPageOptions[this.itemsPerPageSlider];
      this.fetchTrees();
      this.menu = false;
    },

    async exportToPDF() {
      const element = document.querySelector(".v-data-table__wrapper");
      if (!element) return;
      const opt = {
        margin: 1,
        filename: "TreeList.pdf",
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
      };
      await html2pdf().set(opt).from(element).save();
    },

    async printTable() {
      const element = document.querySelector(".v-data-table__wrapper");
      if (!element) return;
      const opt = {
        margin: 1,
        filename: "TreeList.pdf",
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: "in", format: "a4", orientation: "landscape" },
      };
      const worker = await html2pdf().set(opt).from(element).toCanvas().outputPdf("blob");
      const file = new File([worker], "TreeList.pdf", { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(file);
      printJS({ printable: pdfUrl, type: "pdf", showModal: true });
    },
  },
};
</script>

<style lang="scss" scoped>
.custom-btn {
  background-color: #ffffff !important;
  color: #00bd73 !important;
  border: 2px solid #00bd73 !important;
  box-shadow: none !important;

  &:hover {
    background-color: #e0e0e0 !important;
  }
}

.cluster-info-window {
  padding: 4px 8px;
  font-size: 13px;

  strong {
    display: block;
    margin-bottom: 4px;
  }
}

.filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #333;
}

::v-deep .trees-table .v-data-table-header {
  background-color: #1b5e20 !important;

  th {
    color: white !important;
    text-transform: uppercase !important;
    font-weight: 600 !important;

    span {
      color: white !important;
    }

    .v-icon {
      color: white !important;
    }
  }
}

::v-deep .trees-table .v-data-table__checkbox .v-icon {
  color: white !important;
}
</style>
